/* eslint-disable no-use-before-define */
/* eslint-disable import/prefer-default-export */
import { useFileDialog, type UseFileDialogOptions } from '@vueuse/core';
import axios from 'axios';
import {
  computed, ref, unref, watch, type Ref, type UnwrapRef,
} from 'vue';
import { differenceBy } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import imageCompression from 'browser-image-compression';
import { apiUrl } from '@/hooks/fetch';
import { getAuthToken } from '@/utils/tokenManager';
import type { HydraMember } from '@/entities/Entities';

export interface NewFileUpload<T> {
  id: string;
  progress: number;
  file: File;
  isNew: true;
  delete: (url: string) => Promise<void>;
  upload: (url: string) => Promise<T>;
}

export interface ExistingFileUpload<T extends HydraMember> {
  id: string;
  isNew: false;
  file: T;
  delete: (url: string) => Promise<void>;
  upload: (url: string) => Promise<T>;
}

export type FileUpload<T extends HydraMember> = NewFileUpload<T> | ExistingFileUpload<T>;

type UrlSelector<T extends HydraMember, U extends FileUpload<T>> = (upload: U) => string;
type ExistingUrlSelector<T extends HydraMember> = UrlSelector<T, ExistingFileUpload<T>>;
type NewUrlSelector<T extends HydraMember> = UrlSelector<T, NewFileUpload<T>>;

async function uploadFile<T extends HydraMember>(
  upload: NewFileUpload<T>,
  url?: string | NewUrlSelector<T>,
  options?: FileUploadOptions,
) {
  const formData = new FormData();
  const token = getAuthToken();
  const file = await applyTransforms(upload.file, options?.transforms);
  formData.append('file', file, file.name);

  let path = url;
  if (!url) {
    path = '/files';
  } else if (typeof url === 'string') {
    path = url;
  } else {
    path = url(upload);
  }

  const { data } = await axios.post<T>(apiUrl(path), formData, {
    onUploadProgress(progressEvent) {
      upload.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
    },
    headers: {
      accept: 'application/ld+json',
      authorization: `Bearer ${token}`,
      'content-type': 'multipart/form-data',
    },
  });
  return data;
}

async function deleteFile<T extends HydraMember>(
  upload: ExistingFileUpload<T>,
  url?: string | ExistingUrlSelector<T>,
) {
  const token = getAuthToken();
  let path = url;

  if (!url) {
    path = upload.file['@id'];
  } else if (typeof url === 'string') {
    path = url;
  } else {
    path = url(upload);
  }

  await axios.delete<T>(`${apiUrl(path)}`, {
    headers: {
      authorization: `Bearer ${token}`,
    },
  });
}

function isNewFile<T extends HydraMember>(file: File | T): file is File {
  return file instanceof File;
}

export const Upload = {
  from: <T extends HydraMember>(file: File | T, id?: string): FileUpload<T> => {
    if (isNewFile(file)) {
      return {
        id: id ?? uuidv4(),
        progress: 0,
        file,
        isNew: true,
        async delete() {
          return undefined;
        },
        async upload(url: string | NewUrlSelector<T>) {
          return uploadFile(this, url);
        },
      };
    }
    return {
      id: id ?? uuidv4(),
      file,
      isNew: false,
      async delete(url?: string | ExistingUrlSelector<T>) {
        return deleteFile(this, url);
      },
      async upload() {
        return this.file;
      },
    };
  },
};

interface FileUploadOptions {
  dialogOptions?: UseFileDialogOptions | undefined;
  transforms?: {
    matching: RegExp;
    algorithm: (file: File) => Promise<File>;
  }[]
}

const defaultFileUploadOptions: FileUploadOptions = {
  transforms: [
    {
      matching: /^image\/.*$/,
      algorithm(file) {
        return imageCompression(file, {
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        });
      },
    },
  ],
};

async function applyTransforms(file: File, transforms: FileUploadOptions['transforms']) {
  let result = file;
  for (const transform of transforms ?? []) {
    if (transform.matching.test(result.type)) {
      // eslint-disable-next-line no-await-in-loop
      result = await transform.algorithm(file);
    }
  }
  return result;
}

export function useFileUpload<T extends HydraMember>(
  initialFiles: FileUpload<T>[] | Ref<FileUpload<T>[]> = [],
  options: FileUploadOptions | undefined = undefined,
) {
  options = {
    ...defaultFileUploadOptions,
    ...options,
  };
  const files = ref<FileUpload<T>[]>(unref(initialFiles));
  const fileDialog = useFileDialog(options?.dialogOptions);
  const uploadCounter = ref(0);

  watch(
    () => unref(initialFiles),
    (nextFiles, lastFiles) => {
      const removedFiles = differenceBy(lastFiles, nextFiles, (file) => file.file);
      const newFiles = differenceBy(nextFiles, lastFiles, (file) => file.file);

      files.value = differenceBy(files.value, removedFiles, (file) => file.file).concat(
        newFiles as UnwrapRef<FileUpload<T>>[],
      );
    },
  );

  watch(fileDialog.files, (newFiles) => {
    const uploads = [...(newFiles ?? [])].map((file) => Upload.from<T>(file));
    files.value = [...files.value, ...(uploads as UnwrapRef<typeof uploads>)];
  });

  watch(files, (newFiles) => {
    if (!newFiles.length) {
      fileDialog.reset();
    }
  });

  const toDelete = computed(() =>
    differenceBy(unref(initialFiles), files.value, (file) => file.file));

  const toCreate = computed(() =>
    differenceBy(files.value as FileUpload<T>[], unref(initialFiles), (file) => file.file));

  const uploadOne = async <FileT extends HydraMember>(
    upload: FileUpload<FileT>,
    url?: string | NewUrlSelector<FileT>,
  ) => {
    if (!upload.isNew) return upload;
    uploadCounter.value++;
    const data = await uploadFile(upload, url, options);
    uploadCounter.value--;
    return Upload.from(data) as ExistingFileUpload<FileT>;
  };

  const deleteOne = async <FileT extends HydraMember>(
    upload: FileUpload<FileT>,
    url?: string | ExistingUrlSelector<FileT>,
  ) => {
    if (upload.isNew) return;
    uploadCounter.value++;
    await deleteFile(upload, url);
    uploadCounter.value--;
  };

  const uploadFiles = <FileT extends HydraMember>(
    filesToUpload: FileUpload<FileT>[] | FileUpload<FileT>[],
    url?: string | NewUrlSelector<FileT>,
  ) => Promise.all(filesToUpload.map((upload) => uploadOne(upload, url)));

  const deleteFiles = <FileT extends HydraMember>(
    filesToDelete: FileUpload<FileT>[] | FileUpload<FileT>[],
    url?: string | ExistingUrlSelector<FileT>,
  ) => Promise.all(filesToDelete.map((upload) => deleteOne(upload, url)));

  const uploadAll = (url?: string | NewUrlSelector<T>) =>
    uploadFiles(files.value as unknown as FileUpload<T>[], url);

  const deleteAll = (url?: string | ExistingUrlSelector<T>) =>
    deleteFiles(files.value as unknown as FileUpload<T>[], url);

  const deleteDiff = (url?: string | ExistingUrlSelector<T>) =>
    deleteFiles(toDelete.value as unknown as FileUpload<T>[], url);

  const uploadDiff = (url?: string | NewUrlSelector<T>) =>
    uploadFiles(toCreate.value as unknown as FileUpload<T>[], url);

  const updateRemote = async (
    saveUrl?: string | NewUrlSelector<T>,
    deleteUrl?: string | ExistingUrlSelector<T>,
  ) => {
    await deleteFiles(toDelete.value, deleteUrl);
    const newFiles = await uploadFiles(toCreate.value, saveUrl);
    return getUpdatedFiles(newFiles);
  };

  const getUpdatedFiles = (newFiles: ExistingFileUpload<T>[]) =>
    ([] as ExistingFileUpload<T>[]).concat(
      newFiles,
      differenceBy(
        files.value,
        toDelete.value,
        toCreate.value,
        (file) => file.file,
      ) as ExistingFileUpload<T>[],
    );

  const reset = () => {
    fileDialog.reset();
    files.value = [];
  };

  const loading = computed(() => uploadCounter.value !== 0);

  return {
    ...fileDialog,
    files,
    uploadFile: uploadOne,
    uploadFiles,
    uploadAll,
    uploadDiff,
    deleteFile: deleteOne,
    deleteFiles,
    deleteAll,
    deleteDiff,
    updateRemote,
    toCreate,
    toDelete,
    getUpdatedFiles,
    reset,
    loading,
  };
}

export function useSingleFileUpload<T extends HydraMember>(
  initialFile: FileUpload<T> | Ref<FileUpload<T> | undefined> | undefined = undefined,
  options: UseFileDialogOptions | undefined = undefined,
) {
  const uploads = useFileUpload(
    computed(() => {
      const file = unref(initialFile);
      if (!file) return [];
      return [file];
    }),
    { dialogOptions: { multiple: false, ...options } },
  );

  return {
    ...uploads,
    file: computed<FileUpload<T> | undefined>({
      get() {
        return unref(uploads.files)[0] as FileUpload<T>;
      },
      set(upload) {
        if (!upload) uploads.files.value = [];
        else uploads.files.value[0] = upload as UnwrapRef<typeof uploads.files>[number];
      },
    }),
  };
}
